import { formatDateOnlyForPicker, normalizeDateOnly } from './dateOnly';

export const POLICY_ISSUED_DATE_RANGE_PRESETS = [
  { text: 'Last Week', value: 'last_week' },
  { text: 'Last Month', value: 'last_month' },
  { text: 'Last Quarter', value: 'last_quarter' },
  { text: 'Last Year', value: 'last_year' },
  { text: 'Custom Range', value: 'custom' },
];

export const POLICY_ISSUED_DATE_RANGE_PRESET_LABELS = POLICY_ISSUED_DATE_RANGE_PRESETS.reduce(
  (labels, option) => {
    labels[option.value] = option.text;
    return labels;
  },
  {},
);

export const EMPTY_POLICY_ISSUED_DATE_RANGE_FILTER = {
  preset: null,
  startDate: null,
  endDate: null,
};

function toIsoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function getPreviousQuarterRange(referenceDate) {
  const today = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate(),
  );
  const quarterIndex = Math.floor(today.getMonth() / 3);

  if (quarterIndex === 0) {
    return {
      startDate: toIsoDate(new Date(today.getFullYear() - 1, 9, 1)),
      endDate: toIsoDate(new Date(today.getFullYear() - 1, 11, 31)),
    };
  }

  const startMonth = (quarterIndex - 1) * 3;
  return {
    startDate: toIsoDate(new Date(today.getFullYear(), startMonth, 1)),
    endDate: toIsoDate(endOfMonth(new Date(today.getFullYear(), startMonth + 2, 1))),
  };
}

export function resolvePolicyIssuedDateRangePreset(preset, referenceDate = new Date()) {
  if (!preset || preset === 'custom') {
    return { startDate: null, endDate: null };
  }

  const today = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate(),
  );

  if (preset === 'last_week') {
    const start = new Date(today);
    start.setDate(start.getDate() - 6);
    return {
      startDate: toIsoDate(start),
      endDate: toIsoDate(today),
    };
  }

  if (preset === 'last_month') {
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    return {
      startDate: toIsoDate(startOfMonth(lastMonth)),
      endDate: toIsoDate(endOfMonth(lastMonth)),
    };
  }

  if (preset === 'last_quarter') {
    return getPreviousQuarterRange(referenceDate);
  }

  if (preset === 'last_year') {
    const year = today.getFullYear() - 1;
    return {
      startDate: toIsoDate(new Date(year, 0, 1)),
      endDate: toIsoDate(new Date(year, 11, 31)),
    };
  }

  return { startDate: null, endDate: null };
}

export function normalizePolicyIssuedDateRangeFilter(value) {
  if (!value || typeof value !== 'object') {
    return { ...EMPTY_POLICY_ISSUED_DATE_RANGE_FILTER };
  }

  const preset = value.preset || null;
  const startDate = value.startDate ? formatDateOnlyForPicker(value.startDate) || null : null;
  const endDate = value.endDate ? formatDateOnlyForPicker(value.endDate) || null : null;

  return {
    preset,
    startDate: preset === 'custom' ? startDate : null,
    endDate: preset === 'custom' ? endDate : null,
  };
}

export function arePolicyIssuedDateRangeFiltersEqual(a, b) {
  const left = normalizePolicyIssuedDateRangeFilter(a);
  const right = normalizePolicyIssuedDateRangeFilter(b);
  return (
    left.preset === right.preset &&
    (left.startDate || null) === (right.startDate || null) &&
    (left.endDate || null) === (right.endDate || null)
  );
}

export function resolvePolicyIssuedDateRangeForApi(range, referenceDate = new Date()) {
  const normalized = normalizePolicyIssuedDateRangeFilter(range);
  if (!normalized.preset) {
    return { startDate: null, endDate: null };
  }

  if (normalized.preset !== 'custom') {
    return resolvePolicyIssuedDateRangePreset(normalized.preset, referenceDate);
  }

  return {
    startDate: normalizeDateOnly(normalized.startDate),
    endDate: normalizeDateOnly(normalized.endDate),
  };
}

export function hasActivePolicyIssuedDateRangeFilter(range) {
  return Boolean(normalizePolicyIssuedDateRangeFilter(range).preset);
}

export function getPolicyIssuedDateRangeFilterLabel(range) {
  const normalized = normalizePolicyIssuedDateRangeFilter(range);
  if (!normalized.preset) return '';

  if (normalized.preset !== 'custom') {
    return POLICY_ISSUED_DATE_RANGE_PRESET_LABELS[normalized.preset] || normalized.preset;
  }

  const start = normalized.startDate;
  const end = normalized.endDate;
  if (start && end) return `${start} – ${end}`;
  if (start) return `From ${start}`;
  if (end) return `Through ${end}`;
  return POLICY_ISSUED_DATE_RANGE_PRESET_LABELS.custom;
}
